import React from 'react';
import { Category, Languages, ProductApi } from 'common/types';
import { Theme, TextField, InputAdornment, Box, useTheme, Button, Typography } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import Container from 'common/components/Container';

import { useTranslation } from 'services/localization/useTranslation';
import CategorySelector from 'components/CategorySelector';
import { RiSearchLine } from 'react-icons/ri';

interface OwnProps {
	inputValue: string;
	onInputChange: (newInput: string) => void;
	isSearching?: boolean;
	categories: Category[];
	locationStockProducts: ProductApi[];
	selectedCategoryId: string;
	lang: Languages;
	handleSelectedCategoryIdChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

type InputEventType = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

type Props = OwnProps & WithStyles<typeof styles>;

export const RentalSearchBar = ({ ...props }: Props) => {
	const classes = props.classes;
	const { t } = useTranslation();
	const theme = useTheme();

	const label = t('rentals.search.byProductName') + '...';

	return (
		<Container
			classes={{
				root: classes.containerRoot,
			}}
		>
			<TextField
				variant="standard"
				autoFocus
				className={classes.input}
				fullWidth
				placeholder={label}
				value={props.inputValue}
				onChange={(e: InputEventType) => props.onInputChange(e.target.value)}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<RiSearchLine size={17} color={theme.palette.colors.tundra.light} />
						</InputAdornment>
					),
					endAdornment: (
						<>
							{!!props.inputValue.length && (
								<Button variant="text" onClick={() => props.onInputChange('')}>
									<Typography variant="body2">{t('common:actions.clear')}</Typography>
								</Button>
							)}

							<Box className={classes.categorySelector}>
								<CategorySelector
									categories={props.categories}
									stockProducts={props.locationStockProducts}
									selectedCategoryId={props.selectedCategoryId}
									lang={props.lang}
									handleSelectedCategoryIdChange={props.handleSelectedCategoryIdChange}
								/>
							</Box>
						</>
					),
					disableUnderline: true,
				}}
			/>
		</Container>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		containerRoot: {
			padding: theme.spacing(3, 0),
			[theme.breakpoints.down('sm')]: {
				paddingTop: theme.spacing(0),
			},
		},
		input: {
			paddingLeft: theme.spacing(1),
			border: 'solid 1px #999',
			borderRadius: 4,
			background: theme.palette.background.paper,
		},
		spinnerContainer: {
			position: 'relative',
		},
		iconSize: {
			width: theme.spacing(3),
			height: theme.spacing(3),
		},
		icon: {
			position: 'relative',
			top: '-4px',
			left: '4px',
			width: '16px',
			height: '16px',
		},
		categorySelector: {
			color: theme.palette.colors.tundra.light,
			borderLeft: 'solid 1px #999',
			padding: theme.spacing(0, 1),
		},
	});

export default withStyles(styles)(RentalSearchBar);
